import { parseCsv } from '../../../../helpers/csv-helpers';
import { UserOrganizationTypes } from '../../../../models/enums/user-organization-types';

export const USER_GROUP_IMPORT_COLUMNS = ['organization', 'type', 'city', 'state', 'address', 'pta_email', 'contact_email', 'max_age'] as const;

type UserGroupImportColumn = typeof USER_GROUP_IMPORT_COLUMNS[number];

const OPTIONAL_COLUMNS: UserGroupImportColumn[] = ['address', 'pta_email', 'contact_email', 'max_age'];

export type IUserGroupImportRow = Record<UserGroupImportColumn, string>;

export interface IUserGroupImportMalformedRow {
    rowNumber: number,
    reasons: string[],
}

export interface IUserGroupImportScanResult {
    total: number,
    validRows: IUserGroupImportRow[],
    malformedRows: IUserGroupImportMalformedRow[],
}

export interface IUserGroupImportApiRow {
    title: string,
    group_type: string,
    organization_type: string,
    city: string,
    state: string,
    address: string,
    pta_contact_email: string,
    contact_email: string,
    max_age_rating: string,
}

export function toUserGroupImportApiRows(rows: IUserGroupImportRow[]): IUserGroupImportApiRow[] {
    return rows.map(row => ({
        title: row.organization,
        group_type: "organization",
        organization_type: row.type,
        city: row.city,
        state: row.state,
        address: row.address,
        pta_contact_email: row.pta_email,
        contact_email: row.contact_email,
        max_age_rating: row.max_age,
    }));
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const organizationTypes = Object.values(UserOrganizationTypes) as string[];

export function scanUserGroupsCsv(text: string): IUserGroupImportScanResult {
    const [header, ...dataRows] = parseCsv(text);
    if(!header) {
        throw new Error('The file is empty');
    }

    const headerColumns = header.map(h => h.trim().toLowerCase());
    const missingColumns = USER_GROUP_IMPORT_COLUMNS.filter(c => !OPTIONAL_COLUMNS.includes(c) && !headerColumns.includes(c));
    if(missingColumns.length) {
        throw new Error(`Missing columns: ${missingColumns.join(', ')}`);
    }

    const result: IUserGroupImportScanResult = { total: 0, validRows: [], malformedRows: [] };

    dataRows.forEach((fields, i) => {
        if(fields.every(f => !f.trim())) return;

        result.total++;
        // Header is row 1 of the file.
        const rowNumber = i + 2;
        const reasons: string[] = [];

        if(fields.length !== header.length) {
            reasons.push(`expected ${header.length} columns, found ${fields.length}`);
        }

        const row = USER_GROUP_IMPORT_COLUMNS.reduce((acc, column) => {
            acc[column] = (fields[headerColumns.indexOf(column)] || '').trim();
            return acc;
        }, {} as IUserGroupImportRow);

        if(!row.organization) {
            reasons.push('missing organization');
        }

        if(!row.type) {
            reasons.push('missing type');
        }
        else {
            const type = organizationTypes.find(t => t.toLowerCase() === row.type.toLowerCase());
            if(type) {
                row.type = type;
            }
            else {
                reasons.push(`unknown type "${row.type}"`);
            }
        }

        if(!row.city) {
            reasons.push('missing city');
        }

        if(!row.state) {
            reasons.push('missing state');
        }

        if(!row.pta_email && !row.contact_email) {
            reasons.push('missing pta_email or contact_email');
        }
        if(row.pta_email && !EMAIL_REGEX.test(row.pta_email)) {
            reasons.push(`invalid pta_email "${row.pta_email}"`);
        }
        if(row.contact_email && !EMAIL_REGEX.test(row.contact_email)) {
            reasons.push(`invalid contact_email "${row.contact_email}"`);
        }

        if(reasons.length) {
            result.malformedRows.push({ rowNumber, reasons });
        }
        else {
            result.validRows.push(row);
        }
    });

    return result;
}
